/**
 * Updated for new UI design (v2)
 * Changes made:
 * 1. Changed the `search-results` popup position to `fixed`.
 * 2. Dynamically updating popup position to align relative to the `searchbar`.
 */

import classnames from 'classnames';
import React from 'react';

import { objectUtils, commonEnums } from '@codametrix/ui-common';
import { SearchBar } from '@codametrix/ui-components';
import { matchRequirements } from '../../transactions/transaction/show-results';
import { isElementVisible } from '../../../utils/elements';
import { Conditional } from '@codametrix/ui-components';
import { ResultHeaderRow, ResultRow } from './result-row';
import { ResultType } from './result-type';

const { CodeType, SugggestedCodeType } = commonEnums;

const SUGGESTED_LIST_HEADER_BUTTONS_COUNT = 2;

const HEADER_LABEL_MAP = {
  [SugggestedCodeType.CURRENT_CASE]: 'In this case',
  [SugggestedCodeType.RELATED_CASE]: 'In this encounter',
  [SugggestedCodeType.FROM_SOURCES]: 'From sources'
};

const SELECT_ALL_NAME_MAP = {
  [SugggestedCodeType.CURRENT_CASE]: 'caseList',
  [SugggestedCodeType.RELATED_CASE]: 'encounterList',
  [SugggestedCodeType.FROM_SOURCES]: 'codeSourceList'
};

export { ResultType } from './result-type';

type ExtendedSuggestedCodeInfo = {
  codeInfo: CMxCommonApp.SuggestedCode | CMxCommonApp.SuggestedLinkedProcedure;
  searchType: string;
};

// Adding additional metadata to a suggested code so handlers can easily identify the type of code and suggestion list
type SuggestedCodeItem = {
  codeInfo: CMxCommonApp.SuggestedCode | CMxCommonApp.SuggestedLinkedProcedure;
  searchType: string; // cpt, icd, modifier
  suggestedCodeType: commonEnums.SugggestedCodeType;
};

type SuggestedCodeList = {
  encounterList: SuggestedCodeItem[];
  caseList: SuggestedCodeItem[];
  codeSourceList: SuggestedCodeItem[];
};

export type SuggestedCodeLookupProps = {
  searchResults: CMxCommonApp.CodeInfo[];
  searchResultsLookupKey?: string;
  FieldDefinitions: any[];
  autoFocus?: boolean;
  autoBlur?: boolean;
  autoSearch?: boolean;
  actionPrefix?: string;
  placeholderText?: string;
  minLength?: number;
  showSearchButton?: boolean;
  getSelectionData?: (
    codeInfo: CMxCommonApp.CodeInfo | CMxCommonApp.SuggestedCode
  ) => any;
  onAddAllCodes?: (
    codes: CMxCommonApp.CodeInfo[] | CMxCommonApp.SuggestedCode[]
  ) => void;
  onRemoveAllCodes?: (
    codes: CMxCommonApp.CodeInfo[] | CMxCommonApp.SuggestedCode[]
  ) => void;
  onSearch?: CMxCommonApp.CodeSearchHandler;
  shouldShowResults?: CMxCommonApp.CodeSearchStateHandler;
  onSearchSelection?: CMxCommonApp.CodeSearchSelectionHandler;
  handleCodeDelete?: (ev: any) => void;
  debounceTimeout?: number;
  disabled?: boolean;
  actionNameForDD: string;
  SearchBarViewVersion?: CMxCommonApp.SearchBarViewVersion;
  suggestedCodes: CMxCommonApp.SuggestedCodes;
  allowCodeRemove: boolean;
  transactionGuid?: string;
  existingCodes?: CMxCommonApp.ChargeSessionCode[];
};

const defaultSearchContext = (): CMxCommonApp.SearchBarContext => ({
  codeType: null
});

const getSelectionData = (
  codeInfo: CMxCommonApp.CodeInfo | CMxCommonApp.SuggestedCode
) => {
  const searchSelection: CMxCommonApp.CodeInfo = {
    code: codeInfo?.code,
    description: codeInfo?.description,
    codeType: codeInfo?.codeType,
    effectiveDate: '',
    shortDescription: '',
    tenantDefined: false,
    modifierType: codeInfo?.modifierType || null,
    customCpt: codeInfo?.customCpt ?? false,
    codeProvenances: codeInfo?.codeProvenances,
    codeId: codeInfo?.codeId
  };
  return searchSelection;
};

const getSelectionDataForLinkedProcedure = (
  suggestedLinkedProcedure: CMxCommonApp.SuggestedLinkedProcedure
) => {
  const procedureCodeInfo: CMxCommonApp.CodeInfo = {
    code: suggestedLinkedProcedure?.code,
    description: suggestedLinkedProcedure?.description,
    codeType: suggestedLinkedProcedure?.codeType,
    units: suggestedLinkedProcedure?.units,
    effectiveDate: '',
    shortDescription: '',
    tenantDefined: false,
    modifierType: null,
    customCpt: false,
    codeProvenances: suggestedLinkedProcedure?.codeProvenances
  };

  const modifierCodeInfos: CMxCommonApp.CodeInfo[] =
    suggestedLinkedProcedure?.modifiers.map((modifier) => ({
      code: modifier.code,
      description: modifier.description,
      codeType: modifier.codeType,
      effectiveDate: '',
      shortDescription: '',
      tenantDefined: false,
      modifierType: null,
      customCpt: false,
      codeProvenances: modifier.codeProvenances
    }));

  return { procedureCodeInfo, modifierCodeInfos };
};

const placeholderResult = (text: string, FieldDefinitions: any[]) => {
  const styleClasses = ['search-result', 'not-selectable'];

  return (
    <div className={classnames(styleClasses)}>
      {FieldDefinitions?.length > 0 && (
        <div className="col-label search-label">{/* deliberately empty */}</div>
      )}
      <div className="col-code-description">{text}</div>
    </div>
  );
};

const suppressEnterKey = (ev) => {
  // in the case of this component being wrapped by a from, for submit,
  // the form looks for the keydown event in order to submit.  For the lookup
  // form we don't want it to trigger a submit at all.
  if (ev.key === 'Enter') {
    ev.preventDefault();
  }
};

class SuggestedLookup extends React.Component<
  SuggestedCodeLookupProps,
  CMxCommonApp.SuggestedCodeLookupState
> {
  wrapperRef: React.RefObject<HTMLDivElement>;
  resultRef: React.RefObject<HTMLDivElement>;
  searchRef: React.RefObject<SearchBar>;
  searchResultsRef: React.RefObject<HTMLDivElement>;
  suggestedResultsRef: React.RefObject<HTMLDivElement>;

  static displayName = 'SuggestedLookup';

  static defaultProps = {
    autoFocus: false,
    autoBlur: true,
    autoSearch: true,
    actionPrefix: 'Add',
    debounceTimeout: 0,
    placeholderText: 'Search for code',
    minLength: 2,
    FieldDefinitions: [],
    showSearchButton: true,
    actionNameForDD: '~Focus code search',
    searchBarViewVersion: 'SearchBarView1',
    allowCodeRemove: false,
    transactionGuid: '',
    getSelectionData,
    shouldShowResults: (codeQuery: CMxCommonApp.CodeQuery) => true,
    onSearchSelection: (searchSelection: CMxCommonApp.CodeInfo) => {},
    onSearch: (codeQuery: CMxCommonApp.CodeQuery) => {},
    searchBarContext: defaultSearchContext,
    onAddAllCodes: (
      codes: CMxCommonApp.CodeInfo[] | CMxCommonApp.SuggestedCode[]
    ) => {},
    onRemoveAllCodes: (
      codes: CMxCommonApp.CodeInfo[] | CMxCommonApp.SuggestedCode[]
    ) => {}
  };

  constructor(props: SuggestedCodeLookupProps) {
    super(props);

    this.handleSearchSelection = this.handleSearchSelection.bind(this);
    this.getCurrentSuggestedCodeList =
      this.getCurrentSuggestedCodeList.bind(this);
    this.getSuggestedRows = this.getSuggestedRows.bind(this);
    this.moveFocus = this.moveFocus.bind(this);
    this.applySelectedCode = this.applySelectedCode.bind(this);
    this.updatePopupPosition = this.updatePopupPosition.bind(this);
    this.updateSelection = this.updateSelection.bind(this);
    this.toggleSearchAndSuggested = this.toggleSearchAndSuggested.bind(this);
    this.handleSearchBarFocus = this.handleSearchBarFocus.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this._detectClickOutside = this._detectClickOutside.bind(this);
    this.handleFocusExit = this.handleFocusExit.bind(this);
    this.handleOnInputFocus = this.handleOnInputFocus.bind(this);
    this.handleAddAllCodes = this.handleAddAllCodes.bind(this);
    this.handleRemoveAllCodes = this.handleRemoveAllCodes.bind(this);
    this.getBulkOpCodes = this.getBulkOpCodes.bind(this);
    this.handleSuggestedClick = this.handleSuggestedClick.bind(this);

    const WithResults = this.props.searchResults?.length > 0;
    this.state = {
      active: WithResults,
      autocompleteEntry: props.searchResults?.length ? 0 : -1,
      resultsActive: WithResults,
      showResults: WithResults,
      selecting: false,
      searching: false,
      codeQuery: {},
      searchResultPosition: 'bottom',
      resultHeight: 0,
      resultWidth: 0,
      searchBarContext: defaultSearchContext(),
      popupPosition: null,
      searchType: null,
      activeIndex: -1,
      activeSelection: null
    };
    this.wrapperRef = React.createRef();
    this.resultRef = React.createRef();
    this.searchRef = React.createRef();
    this.searchResultsRef = React.createRef();
    this.suggestedResultsRef = React.createRef();

    if (this.props.autoBlur) {
      document.addEventListener('click', this._detectClickOutside, true);
    }

    if (this.props.debounceTimeout > 0) {
      const origSearch = this.handleSearch.bind(this);
      this.handleSearch = objectUtils.debounce(
        origSearch,
        this.props.debounceTimeout
      );
    }
  }

  componentDidMount() {
    // capture: false on scroll — listen only to page-level scroll, not nested
    // containers. With capture: true, layout reflows triggered by add/remove
    // (chip row wraps onto a new line, parent panel scrolls something) fire
    // scroll events that re-anchor the popup to the moved searchRef and make
    // the popup jump. Page scroll still re-positions because it fires on window.
    window.addEventListener('scroll', this.updatePopupPosition);
    window.addEventListener('resize', this.updatePopupPosition);
    // Initial position update
    this.updatePopupPosition();
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.updatePopupPosition);
    window.removeEventListener('resize', this.updatePopupPosition);
  }

  updatePopupPosition = () => {
    if (!this.searchRef.current) {
      return;
    } else {
      const rect = this.searchRef.current.getBoundingClientRect();
      const { resultHeight, resultWidth } = this.state; // Dynamic popup height

      // Hide the popup only when the search bar is scrolled fully off-screen
      // vertically. Horizontal clipping by an `overflow: hidden` ancestor
      // (codes-manager) is intentional; trying to fence the rect inside the
      // viewport there caused the Diagnosis column's popup to never render.
      const isVisible =
        rect.bottom >= 0 && rect.top <= window.innerHeight;

      if (!isVisible) {
        // If search-cols is not visible, hide the popup
        this.setState({
          popupPosition: null // Hide popup
        });
        return;
      }

      // Calculate available space above and below
      const spaceBelow = window.innerHeight - rect.bottom; // Space below search-cols
      const spaceAbove = rect.top; // Space above search-cols
      const spaceRight = window.innerWidth - rect.left; // Space to the right of the element
      const spaceLeft = window.innerWidth - rect.right; // Space to the left of the element

      // Determine where to show the popup: below or above
      const showBelow = spaceBelow >= resultHeight || spaceBelow >= spaceAbove;

      // Decide horizontal positioning (left or right)
      const showLeft = spaceRight >= resultWidth || spaceLeft >= spaceRight;

      // Calculate top and left positions based on the chosen direction
      let top = showBelow
        ? rect.bottom // Position below the element
        : Math.max(0, rect.top - resultHeight); // Position above the element

      let left = showLeft
        ? rect.left // Position to the left of the element
        : Math.max(0, rect.right - resultWidth); // Position to the right of the element
      // Detect if we're inside a transformed parent (like Drawer)
      // by checking if the wrapper has an offsetParent that's not body/html
      const containingBlock = this.getContainingBlock();

      if (containingBlock) {
        const containingRect = containingBlock.getBoundingClientRect();
        // Subtract the containing block's offset to convert from viewport to container coords
        top = top - containingRect.top;
        left = left - containingRect.left;
      }
      // Update the popup's position
      this.setState({
        popupPosition: { top, left }
      });
    }
  };

  // Function to find the containing block
  getContainingBlock = (): HTMLElement | null => {
    if (!this.wrapperRef.current) return null;

    let element = this.wrapperRef.current.parentElement;

    // Traverse up the DOM to find an element that creates a containing block
    while (element && element !== document.body) {
      const style = window.getComputedStyle(element);

      // These CSS properties create a containing block for position:fixed
      if (
        style.transform !== 'none' ||
        style.perspective !== 'none' ||
        style.filter !== 'none' ||
        style.willChange === 'transform' ||
        style.willChange === 'filter' ||
        style.contain === 'paint'
      ) {
        return element;
      }

      element = element.parentElement;
    }

    return null;
  };

  /**
   * Calls onSearch.
   * @param codeQuery
   */
  handleSearch(codeQuery: CMxCommonApp.CodeQuery) {
    codeQuery.context = {
      ...codeQuery.context,
      meetsMinimum: codeQuery?.text?.length > this.props.minLength
    };

    if (codeQuery.context.meetsMinimum) {
      this._setState({
        codeQuery,
        searching: true,
        showResults: this.props.searchResults?.length > 0
      });
    }
    this.props.onSearch(codeQuery);
  }

  private scrollResultIfNeeded() {
    const activeResult = this.resultRef.current?.querySelector(
      '.search-result-submittable'
    );
    if (activeResult) {
      const visibility = isElementVisible(activeResult, this.resultRef.current);
      if (!visibility.contained) {
        if (visibility.below) {
          // only partial support for IE
          // https://caniuse.com/scrollintoview
          activeResult.scrollIntoView({
            block: 'end',
            behavior: 'smooth',
            inline: 'nearest'
          });
        } else {
          activeResult.scrollIntoView({
            block: 'start',
            behavior: 'smooth',
            inline: 'nearest'
          });
        }
      }
    }
  }

  handleOnInputFocus(searchType: string) {
    this.setState({
      searchType
    });
    // Only anchor on the first open. Re-anchoring on every focus event makes
    // the popup chase the search bar when chips are added/removed (the
    // search-bar-container is a flex sibling of .code-list and shifts when
    // the chip row reflows). Window scroll/resize still re-anchor directly.
    if (!this.state.popupPosition) {
      this.updatePopupPosition();
    }
  }

  handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case 'ArrowDown': {
        // Move focus down in the results
        event.preventDefault();
        this.moveFocus(1); // Pass +1 for downward movement
        break;
      }
      case 'ArrowUp': {
        // Move focus up in the results
        event.preventDefault();
        this.moveFocus(-1); // Pass -1 for upward movement
        break;
      }
      case 'Tab': {
        const hasSearchResults = this.props?.searchResults?.length > 0;
        const suggestedResults = this.getCurrentSuggestedCodeList();
        const hasSuggestedResults =
          suggestedResults?.encounterList?.length > 0 ||
          suggestedResults?.caseList?.length > 0;

        // Only handle custom tab logic if both results exist
        if (hasSearchResults && hasSuggestedResults) {
          event.preventDefault();
          this.toggleSearchAndSuggested();
        } else {
          this.handleFocusExit();
        }
        break;
      }
      case 'ArrowLeft': {
        // Optionally toggle back to search if suggested results are shown
        event.preventDefault();
        this.toggleSearchAndSuggested(false); // Pass false to toggle back
        break;
      }
      case 'ArrowRight': {
        // Optionally toggle back to search if suggested results are shown
        event.preventDefault();
        this.toggleSearchAndSuggested(true); // Pass false to toggle back
        break;
      }
      case 'Enter': {
        event.preventDefault();

        const { activeSelection, activeIndex } = this.state;
        const suggestedResults = this.getCurrentSuggestedCodeList();
        const allowHeaderButtons = this.props.allowCodeRemove;

        if (activeSelection === ResultType.SUGGESTED_RESULTS) {
          const caseList = suggestedResults?.caseList || [];
          const relatedList = suggestedResults?.encounterList || [];
          const codeSourceList = suggestedResults?.codeSourceList || [];
          let currentOffset = 0;

          /** Attempts to handle user selection within a given section of suggested codes.
            * section can be a case list or a related case list.
          
            * It handles 3 types of interactions based on the current activeIndex:
            * 1. Header buttons: "Add All" or "Remove All" (if enabled)
            * 2. Individual item selection
            * 3. If nothing matches, it does nothing
          
            * Returns true if an action was taken for this section, otherwise false. 
          */
          const handleSuggestedCodeSection = (
            list: CMxCommonApp.SuggestedCode[],
            type: commonEnums.SugggestedCodeType
          ) => {
            if (!list.length) return false;

            // If header buttons like "Add All" / "Remove All" are shown
            if (allowHeaderButtons) {
              // "Add All" clicked
              if (activeIndex === currentOffset) {
                this.handleAddAllCodes(event, type);
                return true;
              }
              // "Remove All" clicked
              if (activeIndex === currentOffset + 1) {
                this.handleRemoveAllCodes(event, type);
                return true;
              }
              // Move offset past the header buttons
              currentOffset += SUGGESTED_LIST_HEADER_BUTTONS_COUNT;
            }

            // Check if the activeIndex points to a specific item in this section
            const itemIndex = activeIndex - currentOffset;
            if (itemIndex >= 0 && itemIndex < list.length) {
              // Apply the selected code (individual item)
              this.applySelectedCode(event);
              return true;
            }
            // Move offset past the items in this section
            currentOffset += list.length;
            return false;
          };
          if (
            handleSuggestedCodeSection(
              caseList.map((item) => item.codeInfo),
              SugggestedCodeType.CURRENT_CASE
            )
          )
            break;
          if (
            handleSuggestedCodeSection(
              relatedList.map((item) => item.codeInfo),
              SugggestedCodeType.RELATED_CASE
            )
          )
            break;
          if (
            handleSuggestedCodeSection(
              codeSourceList.map((item) => item.codeInfo),
              SugggestedCodeType.FROM_SOURCES
            )
          )
            break;
        }
        this.applySelectedCode(event);
        break;
      }

      case 'Escape': {
        this.handleFocusExit();
        break;
      }
      default:
        break;
    }
  };
  /**
   * Handles moving the keyboard focus up or down through a list of results.
   * It determines the active list (search or suggested), calculates the number
   * of focusable rows (including header buttons, if applicable), and updates
   * the active index accordingly. It ensures the new active item is scrolled into view.
   *
   * @param direction - A number indicating the movement direction: -1 for up, +1 for down
   */

  moveFocus = (direction: number) => {
    const { activeIndex, activeSelection } = this.state;
    const { searchResults } = this.props;
    const suggestedResults = this.getCurrentSuggestedCodeList();

    const hasSearchResults = searchResults?.length > 0;
    const hasCaseResults = suggestedResults?.caseList?.length > 0;
    const hasEncounterResults = suggestedResults?.encounterList?.length > 0;
    // If no active selection is set, initialize it based on available results
    if (activeSelection === undefined || activeSelection === null) {
      if (hasSearchResults) {
        this.updateSelection(ResultType.SEARCH_RESULTS);
        return;
      } else if (hasCaseResults || hasEncounterResults) {
        this.updateSelection(ResultType.SUGGESTED_RESULTS);
        return;
      }
    }

    // Total number of items that can receive focus
    let totalRows = 0;
    // Calculate total rows based on the active result type
    if (activeSelection === ResultType.SEARCH_RESULTS) {
      // Only search result items are focusable (no headers here)
      totalRows = searchResults?.length ?? 0;
    } else if (activeSelection === ResultType.SUGGESTED_RESULTS) {
      // Suggested results can include "Add All" / "Remove All" buttons as well
      const totalCaseRows = hasCaseResults
        ? suggestedResults.caseList?.length ?? 0
        : 0;
      const totalEncounterRows = hasEncounterResults
        ? suggestedResults.encounterList?.length ?? 0
        : 0;
      // Include header buttons if allowed
      const caseHeaderButtons =
        hasCaseResults && this.props.allowCodeRemove
          ? SUGGESTED_LIST_HEADER_BUTTONS_COUNT
          : 0;
      const encounterHeaderButtons =
        hasEncounterResults && this.props.allowCodeRemove
          ? SUGGESTED_LIST_HEADER_BUTTONS_COUNT
          : 0;
      // Final total includes items + headers
      totalRows =
        totalCaseRows +
        caseHeaderButtons +
        totalEncounterRows +
        encounterHeaderButtons;
    }

    if (totalRows === 0) return;
    // Calculate the new active index, wrapping around if needed
    const newIndex = (activeIndex + direction + totalRows) % totalRows;
    // Update the active index, and once the state update is complete,
    // scroll the newly active item into view. The callback ensures scrolling
    // happens *after* React has re-rendered the DOM based on the new index.
    this.setState({ activeIndex: newIndex }, () => {
      this.scrollActiveItemIntoView();
    });
  };

  /**
   * Scrolls the currently active item into view within its container
   * (either search results or suggested results), taking into account
   * a fixed header height that may otherwise cover the item.
   */
  scrollActiveItemIntoView = () => {
    // Get the currently active item (the one with a highlight or focus)
    const activeItem = document.querySelector(
      '.search-result-submittable'
    ) as HTMLElement | null;
    // Height of header or spacing to preserve
    const headerOffset = 60;
    const { activeSelection } = this.state;

    // Determine which container to scroll based on which list is currently active
    const container =
      activeSelection === ResultType.SEARCH_RESULTS
        ? (this.searchResultsRef.current as HTMLElement | null)
        : activeSelection === ResultType.SUGGESTED_RESULTS
        ? (this.suggestedResultsRef.current as HTMLElement | null)
        : null;

    if (!activeItem || !container) return;
    // Calculate position of the active item within the container
    const itemTop = activeItem.offsetTop;
    const itemBottom = itemTop + activeItem.offsetHeight;

    const containerTop = container.scrollTop;
    const containerHeight = container.clientHeight;
    const containerBottom = containerTop + containerHeight;
    // Active item is above the visible area (considering header offset)
    if (itemTop - headerOffset < containerTop) {
      container.scrollTo({
        top: itemTop - headerOffset,
        behavior: 'smooth'
      });
    }
    // Active item is below the visible area
    else if (itemBottom > containerBottom) {
      container.scrollTo({
        top: itemBottom - containerHeight + headerOffset,
        behavior: 'smooth'
      });
    }
  };

  toggleSearchAndSuggested = (toSuggested?: boolean) => {
    const { searchResults } = this.props;
    const { activeSelection } = this.state;
    const suggestedResults = this.getCurrentSuggestedCodeList();
    const hasSearchResults = searchResults?.length > 0;
    const hasSuggestedResults =
      suggestedResults.caseList?.length > 0 ||
      suggestedResults?.encounterList?.length > 0;
    const hasActiveSelection = !!activeSelection; // Compact check for truthy value.

    if (toSuggested !== undefined) {
      if (toSuggested && hasSuggestedResults)
        this.updateSelection(ResultType.SUGGESTED_RESULTS);
      else if (!toSuggested && hasSearchResults)
        this.updateSelection(ResultType.SEARCH_RESULTS);
      return;
    } else {
      if (hasActiveSelection) {
        if (
          activeSelection === ResultType.SEARCH_RESULTS &&
          hasSuggestedResults
        )
          this.updateSelection(ResultType.SUGGESTED_RESULTS);
        else if (
          activeSelection === ResultType.SUGGESTED_RESULTS &&
          hasSearchResults
        )
          this.updateSelection(ResultType.SEARCH_RESULTS);
      } else {
        if (hasSearchResults) this.updateSelection(ResultType.SEARCH_RESULTS);
        else if (hasSuggestedResults)
          this.updateSelection(ResultType.SUGGESTED_RESULTS);
      }
    }
  };

  /**
   * Handles selection of the currently active item (when user presses Enter).
   *
   * Depending on the current active section (`SEARCH_RESULTS` or `SUGGESTED_RESULTS`),
   * it either triggers a search result selection or handles a suggested code selection.
   *
   * - For search results: directly calls `onSearchSelection`.
   * - For suggested results:
   *    - Calculates correct item position accounting for optional header buttons.
   *    - Finds and selects the correct code item.
   *    - If the code is already part of the current transaction and removal is allowed,
   *      triggers `onRemoveAllCodes`.
   *    - Otherwise, adds the selected code via `onSearchSelection`.
   *
   * After handling the selection, it resets the active state.
   */
  applySelectedCode = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const { activeIndex, activeSelection } = this.state;
    const results = this.props.searchResults;
    const suggestedResults = this.getCurrentSuggestedCodeList();
    const allowHeaderButtons = this.props.allowCodeRemove;

    const caseList = suggestedResults?.caseList || [];
    const encounterList = suggestedResults?.encounterList || [];
    const codeSourceList = suggestedResults?.codeSourceList || [];

    let currentOffset = 0;

    if (activeIndex !== -1) {
      // Selection is from search results
      if (activeSelection === ResultType.SEARCH_RESULTS) {
        this.props.onSearchSelection({
          codeInfo: results?.[activeIndex],
          context: {}
        });
      }
      // Selection is from suggested results
      else if (activeSelection === ResultType.SUGGESTED_RESULTS) {
        let activeItem: SuggestedCodeItem | null = null;
        let activeItemType: commonEnums.SugggestedCodeType | null = null;

        /**
         * Finds the item in the given list based on the current activeIndex,
         * adjusting for header buttons and previous list lengths.
         */
        const findItem = (list: SuggestedCodeItem[]): boolean => {
          if (!list.length) return false;
          // If header buttons are rendered for this section, move the offset
          if (allowHeaderButtons)
            currentOffset += SUGGESTED_LIST_HEADER_BUTTONS_COUNT;

          const indexInSection = activeIndex - currentOffset;
          if (indexInSection >= 0 && indexInSection < list.length) {
            activeItem = list[indexInSection];
            return true;
          }
          // Move the offset past this section
          currentOffset += list.length;
          return false;
        };
        // Try to find the selected item in either section
        findItem(caseList) ||
          findItem(encounterList) ||
          findItem(codeSourceList);

        if (activeItem) {
          const transactionGuids =
            activeItem?.codeInfo?.transaction_guids ?? [];
          // Check if the selected code is already part of the current transaction
          const isInCurrentTnx = transactionGuids.includes(
            this.props.transactionGuid
          );

          if (isInCurrentTnx && allowHeaderButtons) {
            // If it's already selected and removal is allowed, remove it
            this.props.onRemoveAllCodes([activeItem.codeInfo]);
          } else {
            // Check if the code is a SuggestedLinkedProcedure
            if (
              'units' in activeItem.codeInfo &&
              'modifiers' in activeItem.codeInfo
            ) {
              // Handle SuggestedLinkedProcedure differently
              const linkedProcedure =
                activeItem.codeInfo as CMxCommonApp.SuggestedLinkedProcedure;

              const { procedureCodeInfo, modifierCodeInfos } =
                getSelectionDataForLinkedProcedure(linkedProcedure);
              const optAdditionalCodes =
                modifierCodeInfos.length > 0 ? [modifierCodeInfos] : undefined;
              this.props.onSearchSelection({
                codeInfo: procedureCodeInfo,
                additionalCodes: optAdditionalCodes,
                context: { source: activeSelection }
              });
            } else {
              // Handle regular SuggestedCode
              this.props.onSearchSelection({
                codeInfo: getSelectionData(activeItem.codeInfo),
                context: { source: activeSelection }
              });
            }
          }
        }
      }
    }
    this.reset();
  };

  updateSelection(
    selection: ResultType.SEARCH_RESULTS | ResultType.SUGGESTED_RESULTS
  ) {
    this.setState({ activeSelection: selection, activeIndex: 0 });
  }

  reset() {
    if (this.searchRef) {
      this.searchRef.current.reset();
    }
    this.setState({
      resultsActive: false,
      showResults: false,
      selecting: false,
      searching: false,
      codeQuery: {},
      activeIndex: -1,
      activeSelection: null,
      searchType: null
    });
  }

  focus() {
    if (this.searchRef.current) {
      this.searchRef.current.focus();
    }
  }

  _detectClickOutside(ev: any) {
    if (this.props.autoBlur) {
      const target = ev?.target;
      //Changed HTMLElement to Element because clicking on an SVG icon (e.g., the delete icon for codes) was not closing the current menu.
      if (target instanceof Element && this.wrapperRef.current !== null) {
        const outOfScope = !this.wrapperRef.current.contains(target);
        if (outOfScope) {
          this._setState({
            searching: false,
            showResults: false,
            active: false,
            searchType: null,
            popupPosition: null
          });
        }
      }
    }
  }

  handleFocusExit() {
    if (this.props.autoBlur) {
      this._setState({
        searching: false,
        showResults: false,
        active: false,
        searchType: null,
        popupPosition: null
      });
    }
  }

  handleSearchChange(event: CustomEvent) {
    if (
      this.state?.searchType === null ||
      this.state.searchType === undefined
    ) {
      this.setState({ searchType: event?.detail?.value?.key });
    }
    if (this.props.autoSearch) {
      const evValues = event.detail.value ?? { value: '', key: '' };
      const codeQuery = {
        text: evValues.value,
        searchType: evValues.key,
        searchResultsLookupKey: this.props.searchResultsLookupKey
      };

      if (event?.detail?.value?.eventSource === 'dropdown') {
        codeQuery.text = '';
        this.state.codeQuery.text = '';
        this.searchRef.current.reset();
      }

      const showResults = matchRequirements(codeQuery);

      if (!showResults) {
        this._setState({
          searching: false,
          showResults: false,
          active: true,
          codeQuery,
          searchBarContext: {
            ...this.state.searchBarContext,
            codeType: event?.detail?.value?.key
          }
        });
      } else if (showResults && !this.state.searching) {
        this._setState({
          searching: true,
          showResults: true,
          active: true,
          codeQuery,
          searchBarContext: {
            ...this.state.searchBarContext,
            codeType: event?.detail?.value?.key
          }
        });
      } else {
        this._setState({
          searching: true,
          showResults: true,
          active: true,
          codeQuery,
          searchBarContext: {
            ...this.state.searchBarContext,
            codeType: event?.detail?.value?.key
          }
        });
      }
      this.handleSearch(codeQuery);
    }
  }

  onSubmit(event: CustomEvent) {
    const evValues = event.detail.value ?? { value: '', key: '' };

    const codeQuery = { text: evValues.value, searchType: evValues.key };
    this.handleSearch(codeQuery);

    if (!this.props.autoSearch) {
      this._setState({
        searching: true,
        showResults: true,
        active: true,
        codeQuery
      });
    }
  }

  componentDidUpdate(
    prevProps: Readonly<SuggestedCodeLookupProps>,
    prevState: Readonly<CMxCommonApp.SuggestedCodeLookupState>
  ): void {
    const { searchResults, suggestedCodes } = this.props;
    const {
      searchResults: previousSearchResults,
      suggestedCodes: prevSuggestedCodes
    } = prevProps;
    const { codeQuery, searchType } = this.state;
    const { searchType: prevSearchType } = prevState;

    if (
      prevState.resultHeight !== this.state.resultHeight ||
      prevState.resultWidth !== this.state.resultWidth
    ) {
      // Only re-position on the first measurement of this open (dimensions
      // transitioning from unmeasured to measured). Skipping subsequent
      // content-driven dimension changes prevents the popup from jumping when
      // the user adds/removes a code and the chip row reflows the searchRef.
      // Window scroll/resize listeners still update position directly.
      const wasUnmeasured =
        !prevState.resultHeight || !prevState.resultWidth;
      if (wasUnmeasured) {
        this.updatePopupPosition();
      }
    }

    if (
      searchResults !== previousSearchResults ||
      suggestedCodes !== prevSuggestedCodes ||
      searchType !== prevSearchType
    ) {
      // if a new result comes in we want to reset the scroll position
      // of the results so that it comes back to the top.
      const height = this.resultRef.current?.getBoundingClientRect()?.height;
      const width = this.resultRef.current?.getBoundingClientRect()?.width;
      this._setState({
        resultHeight: height,
        resultWidth: width
      });

      if (this.state.showResults && this.resultRef.current) {
        this.resultRef.current.scrollTop = 0;
      }

      if (this.state.searching && `${codeQuery.text}`?.length > 1) {
        this._setState({
          searching: false,
          showResults: true,
          resultsActive: true
        });
      }
    }
  }

  _setState(s: Partial<CMxCommonApp.SuggestedCodeLookupState>) {
    this.setState((state) => {
      return { ...state, ...s };
    });
  }

  handleSearchBarFocus(
    ev: React.FocusEvent | React.KeyboardEvent | React.MouseEvent
  ) {
    if (!this.state.active) {
      // The focus event fires after the SearchBar change event, but
      // in the same thread afaict.
      // What this means in practice is that calls to get and set
      // state can have unwanted effects.  Since React's setState queues
      // changes, the desired changes can be overridden if they happen
      // in succession.  By running the call in the next `tick`, then
      // the changes that we're looking for, do not get clobbered.
      setTimeout(() => {
        const showResults = Array.isArray(this.props.searchResults);
        this._setState({ showResults, active: true });
      }, 0);
    }
  }

  handleSearchSelection(
    ev: React.MouseEvent<HTMLDivElement>,
    codeInfo:
      | CMxCommonApp.CodeInfo
      | CMxCommonApp.SuggestedCode
      | CMxCommonApp.SuggestedLinkedProcedure,
    opType: 'add' | 'delete',
    isSuggestedRow?: boolean
  ) {
    ev.preventDefault();
    if (!!codeInfo) {
      const searchSelection = this.props.getSelectionData(codeInfo);
      if (opType === 'delete') {
        this.props.onRemoveAllCodes([searchSelection]);
      } else {
        if ('units' in codeInfo && 'modifiers' in codeInfo) {
          // Handle SuggestedLinkedProcedure differently
          const { procedureCodeInfo, modifierCodeInfos } =
            getSelectionDataForLinkedProcedure(codeInfo);
          const optAdditionalCodes =
            modifierCodeInfos.length > 0 ? [modifierCodeInfos] : undefined;
          this.props.onSearchSelection({
            codeInfo: procedureCodeInfo,
            additionalCodes: optAdditionalCodes,
            context: { 
              source: isSuggestedRow ? ResultType.SUGGESTED_RESULTS : ResultType.SEARCH_RESULTS
            }
          });
        } else {
          // Handle regular SuggestedCode
          this.props.onSearchSelection({
            codeInfo: searchSelection,
            context: { 
              source: isSuggestedRow ? ResultType.SUGGESTED_RESULTS : ResultType.SEARCH_RESULTS
            }
          });
        }
      }
      this.reset();
    }
  }

  getCurrentSuggestedCodeList(): SuggestedCodeList {
    const { searchType } = this.state;
    const { suggestedCodes } = this.props;

    // Safely handle null searchType by using a default empty object
    const suggestedList = (searchType
      ? suggestedCodes[searchType.toLowerCase()]
      : null) ?? {
      encounterList: [],
      caseList: [],
      codeSourceList: []
    };

    const mapList = (
      codelist:
        | CMxCommonApp.SuggestedCode[]
        | CMxCommonApp.SuggestedLinkedProcedure[],
      suggestedCodeType: commonEnums.SugggestedCodeType
    ) =>
      Array.isArray(codelist)
        ? codelist?.map((codeInfo) => ({
            codeInfo,
            searchType,
            suggestedCodeType: suggestedCodeType
          }))
        : [];

    return {
      encounterList: mapList(
        suggestedList?.encounterList ?? [],
        SugggestedCodeType.RELATED_CASE
      ),
      caseList: mapList(
        suggestedList?.caseList ?? [],
        SugggestedCodeType.CURRENT_CASE
      ),
      codeSourceList: mapList(
        suggestedList?.codeSourceList ?? [],
        SugggestedCodeType.FROM_SOURCES
      )
    };
  }

  getBulkOpCodes(opType: string) {
    const suggestedMap = this.getCurrentSuggestedCodeList();
    const suggestedList = suggestedMap[SELECT_ALL_NAME_MAP[opType]] ?? [];
    const codeList = suggestedList?.map((item) => item.codeInfo) ?? [];
    return codeList;
  }

  handleAddAllCodes(
    event: React.SyntheticEvent<HTMLDivElement>,
    opType: string
  ) {
    event.preventDefault();
    const codeList = this.getBulkOpCodes(opType);
    console.log(codeList);
    this.props.onAddAllCodes(codeList);
  }

  handleRemoveAllCodes(
    event: React.SyntheticEvent<HTMLDivElement>,
    opType: string
  ) {
    event.preventDefault();
    const codeList = this.getBulkOpCodes(opType);
    console.log(codeList);
    this.props.onRemoveAllCodes(codeList);
  }

  handleSuggestedClick (
    ev: React.MouseEvent<HTMLDivElement>,
    codeInfo:
      | CMxCommonApp.CodeInfo
      | CMxCommonApp.SuggestedCode
      | CMxCommonApp.SuggestedLinkedProcedure,
    opType: 'add' | 'delete'
  ) {
    this.handleSearchSelection(ev, codeInfo, opType, true);
  };

  getSuggestedRows(
    suggestedList: ExtendedSuggestedCodeInfo[],
    suggestedListType: string,
    indexOffset: number,
    allowCodeRemove: boolean = false
  ) {
    let suggestedResults: React.ReactNode[];
    const headerLabel = HEADER_LABEL_MAP[suggestedListType];
    const sectionStartIndex = indexOffset;

    const selectAllName = SELECT_ALL_NAME_MAP[suggestedListType];

    suggestedResults = suggestedList?.map((result, idx) => {
      // A deactivated shared dx (active === false) lives on the transaction but
      // its chip renders outlined; treat the row as not selected so the next
      // click fires `add` and re-activates it via the shared-code toggle path.
      const isAlreadySelected =
        this.props.existingCodes?.some(
          (ec: any) => {
            if (ec.code !== result.codeInfo.code) return false;
            if (ec.codeId && result.codeInfo.codeId) {
              return ec.codeId === result.codeInfo.codeId && ec.active !== false;
            }
            return ec.active !== false;
          }
        ) ?? false;
      return (
        <ResultRow
          key={idx + result?.codeInfo?.code}
          activeIdx={this.state?.activeIndex}
          activeSelection={this.state?.activeSelection}
          codeInfo={result?.codeInfo}
          idx={
            idx +
            sectionStartIndex +
            (this.props.allowCodeRemove
              ? SUGGESTED_LIST_HEADER_BUTTONS_COUNT
              : 0)
          }
          allowCodeRemove={allowCodeRemove}
          transactionGuid={this.props?.transactionGuid}
          isSuggestedRow={true}
          isAlreadySelected={isAlreadySelected}
          handleClick={this.handleSuggestedClick}
        />
      );
    });
    if (!objectUtils.isEmpty(suggestedResults)) {
      suggestedResults.unshift(
        <ResultHeaderRow
          fieldDefinitions={this.props?.FieldDefinitions}
          label={headerLabel}
          isSuggestedRow={true}
          allowCodeRemove={allowCodeRemove}
          name={`selectall_${selectAllName}_${this.props?.transactionGuid}`}
          opType={suggestedListType}
          handleSelectAll={this.handleAddAllCodes}
          handleUnselectAll={this.handleRemoveAllCodes}
          activeIndex={this.state.activeIndex}
          sectionStartIndex={sectionStartIndex}
          activeSelection={this.state.activeSelection}
        />
      );
    }
    return suggestedResults;
  }

  render() {
    const {
      onSearch,
      searchResults,
      onSearchSelection,
      autoFocus,
      autoBlur,
      shouldShowResults,
      FieldDefinitions,
      placeholderText,
      actionPrefix,
      autoSearch,
      getSelectionData,
      debounceTimeout,
      showSearchButton,
      actionNameForDD,
      suggestedCodes,
      ...rest
    } = this.props;

    const state = this.state;
    const showResults = shouldShowResults(state);

    const styleClasses = [
      'suggested-lookupV2',
      {
        'is-active': this.state.active,
        'is-searching': this.state.searching,
        'is-selecting': this.state.selecting,
        'show-results': showResults,
        'has-search-results': showResults && searchResults?.length > 0
      }
    ];

    let results: React.ReactNode;
    if (showResults && searchResults?.length > 0) {
      results = searchResults.map((result, idx) => (
        <ResultRow
          activeIdx={this.state?.activeIndex}
          activeSelection={this.state?.activeSelection}
          codeInfo={result}
          idx={idx}
          handleClick={this.handleSearchSelection}
        />
      ));

      if (!objectUtils.isEmpty(this.state.codeQuery?.text)) {
        (results as React.ReactElement[]).unshift(
          <ResultHeaderRow
            label={`Result for “${this.state.codeQuery?.text}”`}
            name={`searchResult_${this.props.transactionGuid}`}
            activeIndex={this.state.activeIndex}
            activeSelection={this.state.activeSelection}
          />
        );
      }
    } else if (state.searching) {
      results = placeholderResult('Searching...', FieldDefinitions);
    } else if (showResults) {
      results = placeholderResult(
        `No results found for '${this.state.codeQuery?.text ?? ''}'`,
        FieldDefinitions
      );
    }

    const suggestedCodeList = this.getCurrentSuggestedCodeList();
    const caseRows = this.getSuggestedRows(
      suggestedCodeList?.caseList,
      SugggestedCodeType.CURRENT_CASE,
      0,
      this.props?.allowCodeRemove || true
    );
    const encounterRows = this.getSuggestedRows(
      suggestedCodeList?.encounterList,
      SugggestedCodeType.RELATED_CASE,
      suggestedCodeList?.caseList?.length
        ? suggestedCodeList.caseList.length +
            SUGGESTED_LIST_HEADER_BUTTONS_COUNT
        : 0,
      this.props?.allowCodeRemove || true
    );

    const indexOffset =
      (suggestedCodeList?.caseList?.length || 0) +
      SUGGESTED_LIST_HEADER_BUTTONS_COUNT +
      (suggestedCodeList?.encounterList?.length || 0) +
      SUGGESTED_LIST_HEADER_BUTTONS_COUNT;
    const codeSourceRows = this.getSuggestedRows(
      suggestedCodeList?.codeSourceList,
      SugggestedCodeType.FROM_SOURCES,
      indexOffset,
      false
    );

    return (
      <div
        {...rest}
        onKeyDown={suppressEnterKey}
        onFocus={this.handleSearchBarFocus}
        ref={this.wrapperRef}
        className={classnames(styleClasses)}>
        <div className="search-cols">
          <SearchBar
            ref={this.searchRef}
            flex={true}
            disabled={this.props.disabled}
            actionPrefix={this.props.actionPrefix}
            autoFocus={autoFocus}
            onKeyPress={this.handleKeyPress}
            onChange={this.handleSearchChange}
            onFocus={this.handleOnInputFocus}
            onSubmit={this.onSubmit}
            FieldDefinitions={FieldDefinitions}
            placeholder={this.props.placeholderText}
            showSearchButton={this.props.showSearchButton}
            actionNameForDD={this.props.actionNameForDD}
            searchBarViewVersion={this.props.SearchBarViewVersion}
          />
        </div>
        {this.state.popupPosition && (
          <div
            className="result-container"
            ref={this.resultRef}
            style={{
              top: `${this.state.popupPosition.top}px`, // Dynamic position
              left: `${this.state.popupPosition.left}px` // Dynamic position
            }}>
            <Conditional
              on={!objectUtils.isEmpty(results)}
              style={{ minWidth: '50%' }}>
              <div className="search-results" ref={this.searchResultsRef}>
                {results}
              </div>
            </Conditional>
            <Conditional
              some={[
                !objectUtils.isEmpty(caseRows),
                !objectUtils.isEmpty(encounterRows),
                !objectUtils.isEmpty(codeSourceRows)
              ]}
              style={{ minWidth: '50%' }}>
              <div
                className="suggested-results search-results"
                ref={this.suggestedResultsRef}>
                {caseRows}
                {encounterRows}
                {codeSourceRows}
              </div>
            </Conditional>
          </div>
        )}
      </div>
    );
  }
}

export { SuggestedLookup };
